import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchBundleOffers } from "../lib/api";

const StoreCtx = createContext(null);

const load = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(() => load("hb_cart", []));
  const [wishlist, setWishlist] = useState(() => load("hb_wishlist", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [bundles, setBundles] = useState([]);

  useEffect(() => localStorage.setItem("hb_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("hb_wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => {
    fetchBundleOffers().then(setBundles).catch(() => setBundles([]));
  }, []);

  const addToCart = (product, qty = 1) => {
    setCart((curr) => {
      const existing = curr.find((i) => i.product_id === product.id);
      if (existing) {
        return curr.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...curr,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.images?.[0] || "",
          quantity: qty,
          collections: product.collections || [],
        },
      ];
    });
    setCartOpen(true);
  };

  const updateQty = (id, qty) =>
    setCart((curr) =>
      qty <= 0
        ? curr.filter((i) => i.product_id !== id)
        : curr.map((i) => (i.product_id === id ? { ...i, quantity: qty } : i))
    );

  const removeFromCart = (id) =>
    setCart((curr) => curr.filter((i) => i.product_id !== id));

  const clearCart = () => setCart([]);

  const toggleWishlist = (productId) =>
    setWishlist((curr) =>
      curr.includes(productId) ? curr.filter((id) => id !== productId) : [...curr, productId]
    );

  const isWishlisted = (id) => wishlist.includes(id);

  const subtotal = useMemo(
    () => cart.reduce((s, i) => s + i.price * i.quantity, 0),
    [cart]
  );
  const itemCount = useMemo(
    () => cart.reduce((s, i) => s + i.quantity, 0),
    [cart]
  );

  // Compute best bundle discount across active auto-apply bundles.
  // For each bundle, collect eligible cart items (whose collections include bundle.collection),
  // sum their qty, find the largest tier.qty <= totalQty, and compare tier.price*multiplier vs actual line sum.
  const bundleDiscount = useMemo(() => {
    let totalSavings = 0;
    const applied = [];
    for (const b of bundles) {
      if (!b.auto_apply) continue;
      const tiers = (b.tiers || []).slice().sort((a, b2) => a.qty - b2.qty);
      if (tiers.length === 0) continue;
      const eligible = cart.filter((i) => (i.collections || []).includes(b.collection));
      if (eligible.length === 0) continue;
      const totalQty = eligible.reduce((s, i) => s + i.quantity, 0);
      // Find matching tier: exact match or largest tier that fits (allow multiples of top tier).
      let bestTier = null;
      for (const t of tiers) if (t.qty <= totalQty) bestTier = t;
      if (!bestTier) continue;
      // Bundle covers `bestTier.qty` pieces at `bestTier.price` total.
      // Extra units beyond bestTier.qty stay at full price (line item price).
      // Determine which pieces the bundle applies to — cheapest first (customer-friendly:
      // apply bundle-price to cheapest, extras billed at own price). Actually simpler:
      // apply to whole batch of top `bestTier.qty` cheapest units, savings = sumOfLineItemPricesForThoseUnits - bestTier.price.
      const flat = [];
      for (const i of eligible) {
        for (let k = 0; k < i.quantity; k++) flat.push(i.price);
      }
      flat.sort((a, c) => a - c);
      const covered = flat.slice(0, bestTier.qty);
      const lineTotalForCovered = covered.reduce((s, v) => s + v, 0);
      const savings = Math.max(0, lineTotalForCovered - bestTier.price);
      if (savings > 0) {
        totalSavings += savings;
        applied.push({ id: b.id, name: b.name, collection: b.collection, tier: bestTier, savings });
      }
    }
    return { total: totalSavings, applied };
  }, [cart, bundles]);

  return (
    <StoreCtx.Provider
      value={{
        cart,
        wishlist,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        toggleWishlist,
        isWishlisted,
        subtotal,
        itemCount,
        bundles,
        bundleDiscount,
      }}
    >
      {children}
    </StoreCtx.Provider>
  );
}

export const useStore = () => {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error("useStore must be inside StoreProvider");
  return ctx;
};
